import { createFileRoute, Link, Outlet, useLocation, useNavigate, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { seedDemoProject } from "@/lib/portal/seed.server";
import { Toaster } from "sonner";
import {
  LayoutDashboard, Globe, GitBranch, MessageSquare, Ticket, FolderOpen,
  Receipt, CreditCard, ServerCog, LineChart, Settings, Bell, Zap,
  Sparkles, BookOpen, Gift, Menu, X, LogOut, ChevronRight,
  Shield, Users, Inbox, GitCommitHorizontal, Wallet,
} from "lucide-react";
import { useIsAdmin } from "@/lib/portal/useIsAdmin";

export const Route = createFileRoute("/_authenticated/portal")({
  component: PortalShell,
});

const NAV = [
  { to: "/portal", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/portal/project", label: "Il mio sito", icon: Globe },
  { to: "/portal/messages", label: "Messaggi", icon: MessageSquare },
  { to: "/portal/tickets", label: "Tickets", icon: Ticket },
  { to: "/portal/files", label: "File", icon: FolderOpen },
  { to: "/portal/invoices", label: "Fatture", icon: Receipt },
  { to: "/portal/subscription", label: "Abbonamento", icon: CreditCard },
  { to: "/portal/domains", label: "Dominio & Hosting", icon: ServerCog },
  { to: "/portal/analytics", label: "Analytics", icon: LineChart },
  { to: "/portal/settings", label: "Impostazioni", icon: Settings },
] as const;

const EXTRAS = [
  { to: "/portal/assistant", label: "AI Assistant", icon: Sparkles },
  { to: "/portal/knowledge", label: "Knowledge Base", icon: BookOpen },
  { to: "/portal/upgrades", label: "Area vantaggi", icon: Gift },
] as const;

const ADMIN_NAV = [
  { to: "/portal/admin", label: "Overview", icon: Shield, exact: true },
  { to: "/portal/admin/tickets", label: "Inbox ticket", icon: Inbox },
  { to: "/portal/admin/projects", label: "Progetti & timeline", icon: GitCommitHorizontal },
  { to: "/portal/admin/billing", label: "Fatture & MRR", icon: Wallet },
] as const;

function PortalShell() {
  const router = useRouter();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const { data: isAdmin } = useIsAdmin();

  // Seed demo project on first visit
  const { data: seed } = useQuery({
    queryKey: ["portal", "seed"],
    queryFn: async () => await seedDemoProject(),
    staleTime: Infinity,
  });

  const { data: me } = useQuery({
    queryKey: ["portal", "me"],
    queryFn: async () => {
      const { data } = await supabase.auth.getUser();
      return data.user;
    },
  });

  const { data: notifs } = useQuery({
    queryKey: ["portal", "notifications"],
    queryFn: async () => {
      const { data } = await supabase.from("notifications").select("*").order("created_at", { ascending: false }).limit(12);
      return data ?? [];
    },
    refetchInterval: 30_000,
    enabled: !!seed,
  });

  const unread = (notifs ?? []).filter((n) => !n.read_at).length;

  useEffect(() => setMobileOpen(false), [location.pathname]);

  const signOut = async () => {
    await supabase.auth.signOut();
    qc.clear();
    await router.invalidate();
    navigate({ to: "/auth" });
  };

  const markAllRead = async () => {
    await supabase.from("notifications").update({ read_at: new Date().toISOString() }).is("read_at", null);
    qc.invalidateQueries({ queryKey: ["portal", "notifications"] });
  };

  const displayName = (me?.user_metadata as { full_name?: string } | undefined)?.full_name?.split(" ")[0] ?? me?.email?.split("@")[0] ?? "Cliente";

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white flex">
      <Toaster theme="dark" position="top-right" />
      {/* Ambient glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 h-[500px] w-[500px] rounded-full bg-fuchsia-600/10 blur-3xl" />
        <div className="absolute bottom-0 right-1/4 h-[500px] w-[500px] rounded-full bg-cyan-500/10 blur-3xl" />
      </div>

      {/* Sidebar desktop */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col border-r border-white/5 bg-black/40 backdrop-blur-xl z-30">
        <SidebarInner displayName={displayName} onSignOut={signOut} isAdmin={!!isAdmin} />
      </aside>

      {/* Sidebar mobile */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
              className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 lg:hidden" />
            <motion.aside initial={{ x: -300 }} animate={{ x: 0 }} exit={{ x: -300 }}
              transition={{ type: "spring", damping: 26 }}
              className="fixed inset-y-0 left-0 w-72 bg-[#0a0a0f] border-r border-white/10 z-50 lg:hidden flex flex-col">
              <SidebarInner displayName={displayName} onSignOut={signOut} onClose={() => setMobileOpen(false)} isAdmin={!!isAdmin} />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main */}
      <div className="flex-1 lg:pl-64 relative">
        {/* Header */}
        <header className="sticky top-0 z-20 h-16 border-b border-white/5 bg-[#0a0a0f]/80 backdrop-blur-xl px-4 lg:px-8 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setMobileOpen(true)} className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-white/5">
              <Menu className="h-5 w-5" />
            </button>
            <div className="hidden sm:flex items-center gap-1.5 text-sm text-white/50">
              <Link to="/portal" className="hover:text-white">Portal</Link>
              <ChevronRight className="h-3.5 w-3.5" />
              <span className="text-white">{currentPageLabel(location.pathname)}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <button onClick={() => setNotifOpen((v) => !v)}
                className="relative p-2 rounded-lg hover:bg-white/5">
                <Bell className="h-5 w-5" />
                {unread > 0 && (
                  <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-fuchsia-500 ring-2 ring-[#0a0a0f]" />
                )}
              </button>
              <AnimatePresence>
                {notifOpen && (
                  <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                    className="absolute right-0 mt-2 w-80 rounded-2xl border border-white/10 bg-[#0f0f16]/95 backdrop-blur-xl shadow-2xl overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                      <span className="text-sm font-semibold">Notifiche</span>
                      <button onClick={markAllRead} className="text-xs text-white/60 hover:text-white">Segna tutto letto</button>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {(notifs ?? []).length === 0 && <div className="p-6 text-sm text-white/50 text-center">Nessuna notifica</div>}
                      {(notifs ?? []).map((n) => (
                        <Link key={n.id} to={n.link ?? "/portal"} onClick={() => setNotifOpen(false)}
                          className="block px-4 py-3 hover:bg-white/5 border-b border-white/5 last:border-b-0">
                          <div className="flex items-start gap-2">
                            {!n.read_at && <span className="mt-1.5 h-2 w-2 rounded-full bg-fuchsia-500 shrink-0" />}
                            <div className="min-w-0">
                              <p className="text-sm font-medium">{n.title}</p>
                              {n.body && <p className="text-xs text-white/60 mt-0.5">{n.body}</p>}
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <div className="hidden sm:flex items-center gap-2 pl-2 border-l border-white/10">
              <div className="h-8 w-8 rounded-full bg-gradient-to-br from-fuchsia-500 to-cyan-400 grid place-items-center text-xs font-bold text-black">
                {displayName.slice(0, 1).toUpperCase()}
              </div>
              <div className="text-sm">
                <div className="font-medium leading-tight">{displayName}</div>
                <div className="text-xs text-white/40 leading-tight">Cliente</div>
              </div>
            </div>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8 relative">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function currentPageLabel(pathname: string) {
  const all = [...NAV, ...EXTRAS, ...ADMIN_NAV];
  const match = all.find((n) => (n as { exact?: boolean }).exact ? pathname === n.to : pathname.startsWith(n.to) && n.to !== "/portal");
  return match?.label ?? "Dashboard";
}

function SidebarInner({ displayName, onSignOut, onClose, isAdmin }: { displayName: string; onSignOut: () => void; onClose?: () => void; isAdmin?: boolean }) {
  return (
    <>
      <div className="h-16 px-5 flex items-center justify-between border-b border-white/5">
        <Link to="/portal" className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-fuchsia-500 to-cyan-400 grid place-items-center">
            <Zap className="h-4 w-4 text-black" />
          </div>
          <div>
            <div className="font-semibold leading-tight text-sm">FlashWeb</div>
            <div className="text-[10px] uppercase tracking-widest text-white/40 leading-tight">Portal</div>
          </div>
        </Link>
        {onClose && <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/5"><X className="h-4 w-4" /></button>}
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {isAdmin && (
          <>
            <div className="flex items-center gap-1.5 pb-1 px-3 text-[10px] font-semibold uppercase tracking-widest text-fuchsia-300">
              <Shield className="h-3 w-3" /> Admin Console
            </div>
            {ADMIN_NAV.map((item) => (
              <Link key={item.to} to={item.to} activeOptions={{ exact: (item as { exact?: boolean }).exact }}
                className="group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-white/80 hover:text-white bg-gradient-to-r hover:from-fuchsia-500/10 hover:to-cyan-400/10 transition [&.active]:from-fuchsia-500/20 [&.active]:to-cyan-400/20 [&.active]:text-white [&.active]:bg-gradient-to-r">
                <item.icon className="h-4 w-4" /><span>{item.label}</span>
              </Link>
            ))}
            <div className="pt-4 pb-1 px-3 text-[10px] font-semibold uppercase tracking-widest text-white/30">Vista cliente</div>
          </>
        )}
        {NAV.map((item) => (
          <Link key={item.to} to={item.to} activeOptions={{ exact: (item as { exact?: boolean }).exact }}
            className="group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-white/70 hover:text-white hover:bg-white/5 transition [&.active]:bg-white/10 [&.active]:text-white [&.active]:shadow-inner">
            <item.icon className="h-4 w-4" /><span>{item.label}</span>
          </Link>
        ))}
        <div className="pt-4 pb-1 px-3 text-[10px] font-semibold uppercase tracking-widest text-white/30">Extra premium</div>
        {EXTRAS.map((item) => (
          <Link key={item.to} to={item.to}
            className="group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-white/70 hover:text-white hover:bg-white/5 transition [&.active]:bg-white/10 [&.active]:text-white">
            <item.icon className="h-4 w-4" /><span>{item.label}</span>
          </Link>
        ))}
      </nav>
      <div className="p-3 border-t border-white/5">
        <div className="rounded-2xl p-3 bg-gradient-to-br from-fuchsia-500/10 to-cyan-400/10 border border-white/10">
          <div className="text-xs text-white/60">Loggato come</div>
          <div className="text-sm font-medium truncate">{displayName}</div>
          <button onClick={onSignOut} className="mt-2 w-full flex items-center justify-center gap-1.5 text-xs text-white/70 hover:text-white py-1.5 rounded-lg bg-white/5 hover:bg-white/10 transition">
            <LogOut className="h-3.5 w-3.5" /> Esci
          </button>
        </div>
      </div>
    </>
  );
}

// Small helper used across sub-pages
export const glass = "rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-xl";
